import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PrioSpinner } from '@prio365/prio365-react-library';
import {
  notification,
  Menu,
  Button,
  Row,
  Input,
  Dropdown,
  Modal,
  Col,
  Select,
  TimePicker,
  Form,
} from 'antd';
import { PickerLocale } from 'antd/lib/date-picker/generatePicker';
import moment from 'moment';
import { Moment } from 'moment-timezone';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomSingleDatePicker from '../../../components/CustomSingleDatePicker';
import Flex from '../../../components/Flex';
import useDatePickerLocale from '../../../hooks/useDatePickerLocale';
import { CalendarEvent, ProposedNewTime } from '../../../models/Calendar';
import { ProjectId, MessageId, MailFolderId } from '../../../models/Types';
import { PrioTheme } from '../../../theme/types';
import { rowGutter } from '../../../util/forms';
import {
  apiAcceptEvent,
  apiDeclineEvent,
  apiTentativelyAcceptEvent,
  apiForwardEvent,
  apiDeleteEvent,
} from '../../calendar/api';
import { setMailListNavigationState } from '../actions/actionControllers/mailNavigationActionController';
import { deleteLocalMessage } from '../actions/actionControllers/messageActionController';
import { apiDeleteMessage } from '../api';
import AddressSelectMail from './AddressSelectMail';
import { makePrioStyles } from '../../../theme/utils';
import { QuestionOutlined } from '@ant-design/icons';

const useStyles = makePrioStyles((theme) => ({
  dropdown: {
    '&.ant-dropdown-trigger.ant-btn': {
      backgroundColor: theme.old.palette.backgroundPalette.content,
    },
  },
  button: {
    border: theme.old.borders.content,
  },
  eventMessageViewHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.old.spacing.baseSpacing,
  },
  spinnerWrapper: {
    width: '30px',
    marginRight: '10px',
  },
}));

interface NewProposalForm {
  response: 'decline' | 'tentivlyAccept';
  date: Moment;
  timeRange: [Moment, Moment];
}
interface EventMessageViewHeaderProps {
  projectId: ProjectId;
  messageId: MessageId;
  mailFolderId: MailFolderId;
  event: CalendarEvent;
  prefix?: string;
}

const EventMessageViewHeader: React.FC<EventMessageViewHeaderProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { projectId, messageId, event, mailFolderId } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [answer, setAnswer] = useState<string>(null);
  const [recipients, setRecipients] = useState<string[]>([]);

  const [eventDeleteInProgress, setEventDeleteInProgress] =
    useState<boolean>(false);
  const [showForwardModal, setShowForwardModal] = useState<boolean>(false);
  const [showNewProposalModal, setShowNewProposalModal] =
    useState<boolean>(false);
  const datePickerLocale: PickerLocale = useDatePickerLocale();
  const [form] = Form.useForm<NewProposalForm>();

  const tz = moment.tz.guess();
  const initialValuesNewProposalForm = useMemo(
    () => ({
      date: moment.utc(event.start.dateTime.split('T')[0]),
      timeRange: [
        moment.utc(event.start.dateTime).tz(tz),
        moment.utc(event.end.dateTime).tz(tz),
      ],
    }),
    [event, tz]
  );

  const [isNewProposalFormDirty, setIsNewProposalFormDirty] =
    useState<boolean>(false);
  const [isNewProposalFormComplete, setIsNewProposalFormComplete] =
    useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const removeMailAndNavigateBackToFolder = () => {
    dispatch(setMailListNavigationState(null, projectId));
    dispatch(deleteLocalMessage(projectId, mailFolderId, messageId));
    if (pathname.match(/\/message\/.*\/details?$/)) {
      const pathnameWithoutMessageId = pathname.replace(
        /\/message\/.*\/details?$/,
        ''
      );
      navigate(pathnameWithoutMessageId);
    }
  };

  const handleAccept = async () => {
    setIsWaiting(true);
    const success = await apiAcceptEvent(projectId, messageId, event, answer);
    setIsWaiting(false);
    if (success) {
      removeMailAndNavigateBackToFolder();
    } else {
      notification.open({
        message: t('common:error'),
        description: t('calendar:messages.error.acceptEventError'),
      });
    }
  };

  const handleDecline = async () => {
    setIsWaiting(true);
    const success = await apiDeclineEvent(projectId, messageId, event, answer);
    setIsWaiting(false);
    if (success) {
      removeMailAndNavigateBackToFolder();
    } else {
      notification.open({
        message: t('common:error'),
        description: t('calendar:messages.error.declineEventError'),
      });
    }
  };

  const handleTentativelyAccept = async () => {
    setIsWaiting(true);
    const success = await apiTentativelyAcceptEvent(
      projectId,
      messageId,
      event,
      answer
    );
    setIsWaiting(false);
    if (success) {
      removeMailAndNavigateBackToFolder();
    } else {
      notification.open({
        message: t('common:error'),
        description: t('calendar:messages.error.tentativelyAcceptedEventError'),
      });
    }
  };

  const handleForwardOk = async () => {
    setIsWaiting(true);
    const success = await apiForwardEvent(projectId, event, recipients, answer);
    setIsWaiting(false);
    if (!success) {
      notification.open({
        message: t('common:error'),
        description: t('calendar:messages.error.forwardEventError'),
      });
    }
    setShowForwardModal(false);
    setRecipients([]);
  };

  const handleNewProposalOk = async () => {
    setIsWaiting(true);
    form.validateFields().then();
    const formValues = form.getFieldsValue();
    const newProposal = {
      end: {
        dateTime: `${formValues.date.format('YYYY-MM-DD')}T${formValues.timeRange[1].utc().format('HH:mm')}`,
        timeZone: 'UTC',
      },
      start: {
        dateTime: `${formValues.date.format('YYYY-MM-DD')}T${formValues.timeRange[0].utc().format('HH:mm')}`,
        timeZone: 'UTC',
      },
    } as ProposedNewTime;
    const success =
      formValues.response === 'decline'
        ? await apiDeclineEvent(
            projectId,
            messageId,
            event,
            answer,
            newProposal
          )
        : await apiTentativelyAcceptEvent(
            projectId,
            messageId,
            event,
            answer,
            newProposal
          );
    setIsWaiting(false);
    if (!success) {
      notification.open({
        message: t('common:error'),
        description: t('calendar:messages.error.newProposalError'),
      });
    } else {
      removeMailAndNavigateBackToFolder();
    }
    setShowNewProposalModal(false);
  };

  const handleEventDelete = async () => {
    setEventDeleteInProgress(true);
    const successDeleteEvent = await apiDeleteEvent(projectId, event);
    const successDeleteMessage = await apiDeleteMessage(projectId, messageId);
    if (successDeleteEvent && successDeleteMessage) {
      dispatch(deleteLocalMessage(projectId, mailFolderId, messageId));
    }
    setEventDeleteInProgress(false);
    navigate(`/module/prio/projects/${projectId}/mail/inbox/`);
  };

  const handleForwardCancel = () => {
    setShowForwardModal(false);
    setRecipients([]);
  };

  const handleNewProposalCancel = () => {
    setShowNewProposalModal(false);
  };
  //#endregion

  //#region ------------------------------ Components
  const menu = () => (
    <Menu>
      <Menu.Item
        onClick={(e) => {
          e.domEvent.stopPropagation();
          setShowForwardModal(true);
        }}
      >
        {t('mail:eventMessage.actions.forward')}
      </Menu.Item>
      <Menu.Item
        onClick={(e) => {
          e.domEvent.stopPropagation();
          setShowNewProposalModal(true);
        }}
      >
        {t('mail:eventMessage.actions.newProposal')}
      </Menu.Item>
    </Menu>
  );

  const renderEventDeleteButton = () => (
    <>
      <Button
        className={classes.button}
        disabled={!event?.id || isWaiting || eventDeleteInProgress}
        onClick={handleEventDelete}
      >
        <Row>
          {eventDeleteInProgress === false ? (
            <></>
          ) : (
            <div className={classes.spinnerWrapper}>
              <PrioSpinner size="small" alignSelf></PrioSpinner>
            </div>
          )}
          {t('mail:eventMessage.actions.deleteEvent')}
        </Row>
      </Button>
    </>
  );

  const renderEventHeader = () => (
    <>
      <Input
        onChange={(e) => setAnswer(e.target.value)}
        placeholder={t('mail:eventMessage.actions.answerPlaceholder')}
      />
      <Flex.Item>
        <Flex.Row
          childrenGap={theme.old.spacing.baseSpacing}
          justifyContent="flex-end"
        >
          <Button
            onClick={() => handleAccept()}
            className={classes.button}
            disabled={!event?.id || isWaiting}
          >
            <FontAwesomeIcon icon={['fal', 'check']} />
            {t('mail:eventMessage.actions.accept')}
          </Button>
          <Button
            onClick={() => handleTentativelyAccept()}
            icon={<QuestionOutlined />}
            className={classes.button}
            disabled={!event?.id || isWaiting}
          >
            {t('mail:eventMessage.actions.tenativelyAccepted')}
          </Button>
          <Button
            icon={<FontAwesomeIcon icon={['fal', 'times']} />}
            onClick={() => handleDecline()}
            className={classes.button}
            disabled={!event?.id || isWaiting}
          >
            {t('mail:eventMessage.actions.declined')}
          </Button>
          <Dropdown
            overlay={menu()}
            trigger={['click']}
            placement="bottomRight"
            className={classes.dropdown}
            disabled={!event?.id || isWaiting}
          >
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className={classes.button}
              icon={<FontAwesomeIcon icon={['fal', 'ellipsis-h']} />}
            />
          </Dropdown>
        </Flex.Row>
      </Flex.Item>
    </>
  );

  const renderEventActions = () => {
    if (event) {
      if (event.isCancelled) return renderEventDeleteButton();
      else return renderEventHeader();
    }
    return null;
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion
  return (
    <div className={classes.eventMessageViewHeader}>
      {renderEventActions()}
      <Modal
        visible={showForwardModal}
        title={t('calendar:eventOverview.forwardModal.title')}
        okText={t('calendar:eventOverview.forwardModal.okText')}
        cancelText={t('calendar:eventOverview.forwardModal.cancelText')}
        onOk={() => handleForwardOk()}
        okButtonProps={{ disabled: !event?.id || isWaiting }}
        onCancel={() => handleForwardCancel()}
      >
        <Form layout="vertical">
          <Row gutter={rowGutter}>
            <Col span={24}>
              <Form.Item
                label={t('calendar:eventOverview.forwardModal.secondaryTitle')}
              >
                <AddressSelectMail
                  projectId={projectId}
                  value={recipients}
                  changeHandler={setRecipients}
                  closableTags
                  bordered
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        visible={showNewProposalModal}
        title={t('calendar:eventOverview.newProposalModal.title')}
        okText={t('calendar:eventOverview.newProposalModal.okText')}
        cancelText={t('calendar:eventOverview.newProposalModal.cancelText')}
        onOk={() => handleNewProposalOk()}
        okButtonProps={{
          disabled:
            !event?.id ||
            isWaiting ||
            !isNewProposalFormDirty ||
            !isNewProposalFormComplete,
        }}
        onCancel={() => handleNewProposalCancel()}
      >
        <Form<NewProposalForm>
          form={form}
          layout="vertical"
          onValuesChange={(_, values) => {
            const _date = moment(values.date.toISOString());
            const sameDay = _date.isSame(
              initialValuesNewProposalForm.date,
              'day'
            );

            const [start, end] = values.timeRange;
            const _start = moment(start.toISOString());
            const _end = moment(end.toISOString());
            const [initStart, initEnd] = initialValuesNewProposalForm.timeRange;

            const sameStart = _start.isSame(initStart, 'minute');
            const sameEnd = _end.isSame(initEnd, 'minute');

            const isDirty = !sameDay || !(sameStart && sameEnd);
            setIsNewProposalFormDirty(isDirty);
            setIsNewProposalFormComplete(
              !Object.values(values).some((v) => !v)
            );
          }}
          initialValues={initialValuesNewProposalForm}
        >
          <Form.Item
            label={t(
              'calendar:eventOverview.newProposalModal.label.existingInvitation'
            )}
            name="response"
          >
            <Select
              showSearch={false}
              placeholder={t(
                'calendar:eventOverview.newProposalModal.label.existingInvitationPlaceholder'
              )}
              options={[
                {
                  value: 'tenativelyAccepted',
                  label: t(
                    'calendar:eventOverview.newProposalModal.label.tenativelyAccepted'
                  ),
                },
                {
                  value: 'decline',
                  label: t(
                    'calendar:eventOverview.newProposalModal.label.decline'
                  ),
                },
              ]}
            ></Select>
          </Form.Item>
          <Flex.Row childrenGap={theme.spacing.small}>
            <Flex.Item flex={1}>
              <Form.Item
                label={t('calendar:eventOverview.newProposalModal.label.date')}
                name="date"
              >
                <CustomSingleDatePicker
                  id="newMeetingProposal"
                  anchorDirection={'ANCHOR_RIGHT'}
                  small={true}
                  regular={false}
                  twoMonths={false}
                  withFullScreenPortal={false}
                  daySize={30}
                  hideKeyboardShortcutsPanel={true}
                  showDefaultInputIcon={true}
                  inputIconPosition={'after'}
                  blockDatesAfter={moment()}
                />
              </Form.Item>
            </Flex.Item>
            <Flex.Item flex={1}>
              <Form.Item
                label={t(
                  'calendar:eventOverview.newProposalModal.label.timeRange'
                )}
                name="timeRange"
              >
                <TimePicker.RangePicker
                  locale={datePickerLocale}
                  style={{ width: '100%' }}
                  disabledHours={() => [0, 1, 2, 3, 4, 5, 21, 22, 23]}
                  disabledMinutes={(selectedHour: number) => {
                    if (selectedHour === 20) {
                      return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
                    } else {
                      return [];
                    }
                  }}
                  //@ts-ignore
                  picker="time"
                  format="HH:mm"
                  suffixIcon={null}
                  minuteStep={5}
                  allowClear={false}
                  showNow={false}
                />
              </Form.Item>
            </Flex.Item>
          </Flex.Row>
        </Form>
      </Modal>
    </div>
  );
};

export default EventMessageViewHeader;
